import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Send, Loader2, Check, Eye } from "lucide-react";
import { toast } from "sonner";

/**
 * DailyEmailCard — subscription toggle for the automated daily P&L email.
 * Loads the current user's `/reports/email/me` state, lets them toggle
 * active, choose time-of-day + timezone, and fire a manual send-now.
 */
export default function DailyEmailCard() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [timeVal, setTimeVal] = useState("06:00");
  const [preview, setPreview] = useState(null); // {subject, html, text, date, totals}
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDate, setPreviewDate] = useState(() => {
    const d = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    return d;
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/email/me");
      setSub(data);
      setTimeVal(data.send_time_local || "06:00");
    } catch (e) {
      toast.error("Could not load email settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/reports/email/me", patch);
      setSub((prev) => ({ ...prev, ...data }));
      return data;
    } finally {
      setSaving(false);
    }
  };

  const onToggle = async (active) => {
    try {
      const next = await save({ active });
      toast.success(
        active
          ? `Daily email on · ${next.send_time_local} ${short(next.timezone)}`
          : "Daily email off"
      );
    } catch {
      toast.error("Save failed");
    }
  };

  const onTimeCommit = async () => {
    if (!sub || timeVal === sub.send_time_local) return;
    if (!/^\d{2}:\d{2}$/.test(timeVal)) {
      toast.error("Use HH:MM (24h)");
      return;
    }
    try {
      await save({ send_time_local: timeVal });
      toast.success(`Time updated · ${timeVal}`);
    } catch {
      toast.error("Save failed");
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await api.post("/reports/email/send-now");
      toast.success("Email sent — check your inbox");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Send failed";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const openPreview = async (dateOverride) => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const { data } = await api.post("/reports/email/preview", {
        date: dateOverride || previewDate,
      });
      setPreview(data);
      if (data.date) setPreviewDate(data.date);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Preview failed";
      toast.error(msg);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="surface-card p-5 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading email settings…
      </div>
    );
  }

  const configured = sub?.provider_configured;
  const active = !!sub?.active;

  return (
    <div className="surface-card p-5" data-testid="daily-email-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
            <Mail className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div>
            <div className="label-eyebrow">Daily email</div>
            <div className="font-heading text-lg font-semibold mt-0.5">
              Automated P&amp;L snapshot to your inbox
            </div>
            <p className="text-xs text-muted-foreground mt-1 max-w-md">
              Get a clean daily report — revenue, COGS, expenses, profit, orders and
              deliveries — sent every day at your chosen local time.
            </p>
          </div>
        </div>
        <Switch
          checked={active}
          onCheckedChange={onToggle}
          disabled={!configured || saving}
          data-testid="daily-email-toggle"
          aria-label="Toggle daily email"
        />
      </div>

      {!configured && (
        <div className="mt-4 text-xs bg-[#E5A93D]/10 border border-[#E5A93D]/40 text-[#7c5a14] rounded px-3 py-2">
          Mailgun isn&apos;t configured yet. Once <code className="font-mono">MAILGUN_API_KEY</code> +
          {" "}<code className="font-mono">MAILGUN_DOMAIN</code> are set and
          {" "}<code className="font-mono">mg.cabinetdepot.com</code> is verified in Mailgun,
          the toggle will enable.
        </div>
      )}

      {active && configured && (
        <div className="mt-5 grid gap-4 sm:grid-cols-[auto_1fr_auto_auto] items-end">
          <div>
            <Label className="text-[11px] label-eyebrow">Send time (local)</Label>
            <Input
              type="time"
              value={timeVal}
              onChange={(e) => setTimeVal(e.target.value)}
              onBlur={onTimeCommit}
              className="w-32 mt-1"
              data-testid="daily-email-time"
            />
          </div>
          <div className="text-xs text-muted-foreground">
            <div className="label-eyebrow">Time zone</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono text-foreground">{sub.timezone}</span>
              {sub.last_sent_date && (
                <span className="inline-flex items-center gap-1 text-[10px] text-[#1f5f47]">
                  <Check className="w-3 h-3" /> last sent {sub.last_sent_date}
                </span>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => openPreview()}
            data-testid="daily-email-preview"
          >
            <Eye className="w-4 h-4 mr-2" /> Preview
          </Button>
          <Button
            variant="outline"
            onClick={sendNow}
            disabled={sending}
            data-testid="daily-email-send-now"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send test now
          </Button>
        </div>
      )}

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          className="max-w-3xl w-[95vw] max-h-[90vh] flex flex-col p-0 overflow-hidden"
          data-testid="daily-email-preview-dialog"
        >
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-border/60 shrink-0">
            <DialogTitle className="flex items-center gap-2 font-heading">
              <Eye className="w-4 h-4" /> Email preview
            </DialogTitle>
            <div className="flex items-end gap-3 mt-3">
              <div>
                <Label className="text-[11px] label-eyebrow">Business day</Label>
                <Input
                  type="date"
                  value={previewDate}
                  onChange={(e) => setPreviewDate(e.target.value)}
                  className="w-40 mt-1"
                  data-testid="daily-email-preview-date"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openPreview(previewDate)}
                disabled={previewLoading}
                data-testid="daily-email-preview-refresh"
              >
                {previewLoading ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Eye className="w-3.5 h-3.5 mr-1.5" />
                )}
                Render
              </Button>
              {preview?.subject && (
                <div className="text-xs text-muted-foreground truncate">
                  <span className="label-eyebrow mr-1">Subject:</span>
                  <span className="text-foreground">{preview.subject}</span>
                </div>
              )}
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-[#f6f4ee]">
            {previewLoading && !preview ? (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Rendering…
              </div>
            ) : preview?.html ? (
              <iframe
                title="Email preview"
                srcDoc={preview.html}
                sandbox=""
                className="w-full h-full min-h-[60vh] border-0 bg-white"
                data-testid="daily-email-preview-frame"
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function short(tz) {
  if (!tz) return "";
  const parts = tz.split("/");
  return parts[parts.length - 1].replace(/_/g, " ");
}
